import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, Play, Plus, Save, Trash2, X } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import CustomSelect from "../../components/form/CustomSelect";
import { formatDate } from "./shared";
import {
  DEFAULT_SETTINGS,
  MATCH_TYPES,
  PROFILE_ROLES,
  ROLE_LABEL,
  fontToSize,
  ruleFor,
  sizeToFont,
} from "./profileRules";

/**
 * Edit an import profile against a sample sheet: the layer rules are previewed
 * on the sample's real layers, the legend lists the colours its zoning layers
 * actually use, the label sizes come from its text. Saving bumps the version;
 * "Apply to the sample" puts the profile on that import to build and check.
 */

const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString("en-IN"));
const swatch = (fill, size = 16) => (
  <span
    style={{ display: "inline-block", width: size + 6, height: size, borderRadius: 3, background: fill, border: "1px solid rgba(0,0,0,.25)", verticalAlign: "middle" }}
  />
);

export default function ProfileEditor() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [profile, setProfile] = useState(null);
  const [meta, setMeta] = useState({ name: "", authority: "", description: "" });
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [sampleOptions, setSampleOptions] = useState([]);
  const [sampleId, setSampleId] = useState("");
  const [sample, setSample] = useState(null);

  const load = useCallback(async () => {
    const res = await Axios.get(`/maps/gis-imports/profiles/${profileId}`, authHeaders());
    const p = res.data?.data;
    setProfile(p);
    setMeta({ name: p.name, authority: p.authority, description: p.description || "" });
    setSettings({ ...DEFAULT_SETTINGS, ...p.settings });
    setDirty(false);
    return p;
  }, [profileId]);

  useEffect(() => {
    load()
      .then((p) =>
        Axios.get("/maps/gis-imports", {
          params: { limit: 100, include_batches: true, with_analysis: true, authority: p.authority },
          ...authHeaders(),
        })
      )
      .then((res) => {
        const rows = res.data?.data?.data || [];
        setSampleOptions(rows.map((r) => ({ value: r.id, label: `${r.map?.title} · ${r.plan_name || ""} · ${r.status}` })));
        if (rows[0]) setSampleId((current) => current || rows[0].id);
      })
      .catch((err) => toast.error(err.response?.data?.message || "Could not load the profile"));
  }, [load, toast]);

  useEffect(() => {
    if (!sampleId) return;
    setSample(null);
    Axios.get(`/maps/gis-imports/${sampleId}`, authHeaders())
      .then((res) => setSample(res.data?.data))
      .catch(() => toast.error("Could not load the sample sheet"));
  }, [sampleId, toast]);

  const change = (fn) => {
    setSettings((s) => fn(structuredClone(s)));
    setDirty(true);
  };

  // ── What the rules do to the sample ──
  const sampleLayers = useMemo(() => (sample?.analysis?.layers || []).filter((l) => l.features > 0), [sample]);
  const decided = useMemo(
    () => sampleLayers.map((l) => ({ layer: l, hit: ruleFor(settings.layer_rules, l.name) })),
    [sampleLayers, settings.layer_rules]
  );
  const zoneColours = useMemo(() => {
    const merged = new Map();
    for (const { layer, hit } of decided) {
      if (hit?.rule.role !== "dp_zones") continue;
      for (const c of layer.colours) {
        if (!c.fill) continue;
        const prev = merged.get(c.fill) || { fill: c.fill, n: 0, hectares: 0, layers: new Set() };
        prev.n += c.n;
        prev.hectares += c.hectares || 0;
        prev.layers.add(layer.name);
        merged.set(c.fill, prev);
      }
    }
    return [...merged.values()].sort((a, b) => b.hectares - a.hectares);
  }, [decided]);
  const sheetLegend = useMemo(
    () => Object.fromEntries((sample?.analysis?.sheet_legend || []).map((l) => [l.fill, l])),
    [sample]
  );
  const legendFills = new Set(settings.legend.map((l) => l.fill));
  const textSizes = (sample?.analysis?.text || []).filter((t) => t.inside_map > 0);

  // ── Rules ──
  const addRule = (rule = { match: "contains", value: "", role: "dp_zones" }) =>
    change((s) => {
      s.layer_rules.push(rule);
      return s;
    });
  const setRule = (i, patch) =>
    change((s) => {
      s.layer_rules[i] = { ...s.layer_rules[i], ...patch };
      return s;
    });
  const moveRule = (i, d) =>
    change((s) => {
      const j = i + d;
      if (j < 0 || j >= s.layer_rules.length) return s;
      [s.layer_rules[i], s.layer_rules[j]] = [s.layer_rules[j], s.layer_rules[i]];
      return s;
    });
  const removeRule = (i) =>
    change((s) => {
      s.layer_rules.splice(i, 1);
      return s;
    });

  // ── Legend ──
  const addColour = (fill) =>
    change((s) => {
      const hint = sheetLegend[fill];
      s.legend.push({ fill, code: hint?.code || "", name: hint?.name || "" });
      return s;
    });
  const setColour = (i, patch) =>
    change((s) => {
      s.legend[i] = { ...s.legend[i], ...patch };
      return s;
    });
  const removeColour = (i) =>
    change((s) => {
      s.legend.splice(i, 1);
      return s;
    });

  const save = async () => {
    setBusy("save");
    try {
      const res = await Axios.put(
        `/maps/gis-imports/profiles/${profileId}`,
        { ...meta, settings },
        authHeaders()
      );
      const p = res.data?.data;
      setProfile(p);
      setSettings({ ...DEFAULT_SETTINGS, ...p.settings });
      setDirty(false);
      toast.success(`Saved — version ${p.version}.`);
      return p;
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save");
      return null;
    } finally {
      setBusy(null);
    }
  };

  const applyToSample = async () => {
    if (dirty && !(await save())) return;
    setBusy("apply");
    try {
      await Axios.post(`/maps/gis-imports/${sampleId}/apply-profile`, { profile_id: profileId }, authHeaders());
      toast.success("Profile applied. Build the draft to check it.");
      navigate(`/gis-imports/${sampleId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not apply the profile");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await Axios.delete(`/maps/gis-imports/profiles/${profileId}`, authHeaders());
      navigate("/gis-imports/profiles");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not delete");
    }
  };

  if (!profile) {
    return (
      <div className="bg-white rounded-2 common-shadow mt-4 p-4 text-center">
        <Spinner />
      </div>
    );
  }

  const layerNames = sampleLayers.map((l) => l.name);

  return (
    <>
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <Button variant="link" className="p-0 d-flex align-items-center gap-1 mb-3" onClick={() => navigate("/gis-imports/profiles")}>
          <ArrowLeft size={16} /> All profiles
        </Button>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <h5 className="fw-semibold mb-0">{profile.name}</h5>
          <Badge bg="light" text="dark" className="fw-medium">v{profile.version}</Badge>
          {dirty && <Badge bg="warning" className="fw-medium">Unsaved changes</Badge>}
          <span className="text-muted small ms-auto">updated {formatDate(profile.updated_at)}</span>
        </div>
        <div className="row g-3" style={{ maxWidth: 900 }}>
          <div className="col-md-5">
            <Form.Label className="small fw-medium">Name</Form.Label>
            <Form.Control value={meta.name} onChange={(e) => { setMeta((m) => ({ ...m, name: e.target.value })); setDirty(true); }} />
          </div>
          <div className="col-md-2">
            <Form.Label className="small fw-medium">Authority</Form.Label>
            <Form.Control value={meta.authority} maxLength={30} onChange={(e) => { setMeta((m) => ({ ...m, authority: e.target.value.toUpperCase() })); setDirty(true); }} />
          </div>
          <div className="col-md-5">
            <Form.Label className="small fw-medium">Description</Form.Label>
            <Form.Control value={meta.description} placeholder="Which sheets this is for" onChange={(e) => { setMeta((m) => ({ ...m, description: e.target.value })); setDirty(true); }} />
          </div>
          <div className="col-12">
            <CustomSelect
              label={{ name: "Sample sheet (to preview the rules on)" }}
              options={sampleOptions}
              value={sampleOptions.find((o) => o.value === sampleId) || null}
              onChange={(o) => setSampleId(o?.value || "")}
              placeholder={`An import of a ${meta.authority} sheet that has been read`}
              noOptionsMessage={() => `No ${meta.authority} sheet has been read yet — start an import of one first`}
            />
          </div>
        </div>
      </div>

      {/* ── 1. Layer rules ── */}
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <h6 className="fw-semibold mb-1">1. Layer rules</h6>
        <p className="text-muted small mb-3">
          Which PDF layers become which map layer. Rules are checked top to bottom; the first that matches a layer
          decides it, and a layer no rule matches is not imported. Use "contains" or a pattern so one rule covers every
          sheet (e.g. <code>^01_.*zone$</code>).
        </p>
        <datalist id="profile-layer-names">
          {layerNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <Table responsive size="sm" className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 32 }}>#</th>
              <th style={{ minWidth: 150 }}>Match</th>
              <th style={{ minWidth: 280 }}>Value</th>
              <th style={{ minWidth: 200 }}>Import as</th>
              <th style={{ minWidth: 110 }} />
              <th style={{ minWidth: 90 }} className="text-end">On sample</th>
              <th style={{ minWidth: 90 }} />
            </tr>
          </thead>
          <tbody>
            {settings.layer_rules.length === 0 && (
              <tr>
                <td colSpan={7} className="text-muted small">No rules yet — add one, or use "Add as" on the sample's layers below.</td>
              </tr>
            )}
            {settings.layer_rules.map((r, i) => {
              const hits = decided.filter((d) => d.hit?.index === i).length;
              return (
                <tr key={i}>
                  <td className="text-muted">{i + 1}</td>
                  <td>
                    <Form.Select size="sm" value={r.match} onChange={(e) => setRule(i, { match: e.target.value })}>
                      {MATCH_TYPES.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control size="sm" list="profile-layer-names" value={r.value} onChange={(e) => setRule(i, { value: e.target.value })} />
                  </td>
                  <td>
                    <Form.Select size="sm" value={r.role} onChange={(e) => setRule(i, { role: e.target.value })}>
                      {PROFILE_ROLES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    {r.role === "tp_plots" && (
                      <Form.Control size="sm" placeholder="TP name" value={r.tp_name || ""} onChange={(e) => setRule(i, { tp_name: e.target.value })} />
                    )}
                    {r.role === "roads" && (
                      <Form.Control size="sm" type="number" min={0} placeholder="Width (m)" value={r.width_m ?? ""} onChange={(e) => setRule(i, { width_m: e.target.value })} />
                    )}
                  </td>
                  <td className={`text-end small ${sample && hits === 0 ? "text-warning-emphasis" : "text-muted"}`}>
                    {sample ? `${hits} layer${hits === 1 ? "" : "s"}` : "-"}
                  </td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="link" className="p-1" title="Move up" onClick={() => moveRule(i, -1)}><ArrowUp size={14} /></Button>
                    <Button size="sm" variant="link" className="p-1" title="Move down" onClick={() => moveRule(i, 1)}><ArrowDown size={14} /></Button>
                    <Button size="sm" variant="link" className="p-1 text-danger" title="Remove" onClick={() => removeRule(i)}><X size={14} /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-primary" className="d-flex align-items-center gap-1" onClick={() => addRule()}>
          <Plus size={14} /> Add rule
        </Button>

        {sample && (
          <>
            <h6 className="fw-semibold mt-4 mb-2 small text-uppercase text-muted">On the sample sheet: {sample.map?.title}</h6>
            <Table responsive size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>PDF layer</th>
                  <th className="text-end">Shapes</th>
                  <th>Colours</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {decided.map(({ layer, hit }) => (
                  <tr key={layer.name} className={hit && hit.rule.role !== "ignore" ? "" : "text-muted"}>
                    <td className="small fw-medium text-break">{layer.name}</td>
                    <td className="text-end small">{fmt(layer.features)}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {layer.colours.slice(0, 8).map((c) => (
                          <span key={c.fill} title={`${c.fill} · ${fmt(c.n)} shapes`}>{swatch(c.fill, 12)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="small">
                      {hit ? (
                        <>
                          {ROLE_LABEL[hit.rule.role]} <span className="text-muted">(rule {hit.index + 1})</span>
                        </>
                      ) : (
                        <Form.Select
                          size="sm"
                          value=""
                          style={{ maxWidth: 220 }}
                          onChange={(e) => e.target.value && addRule({ match: "exact", value: layer.name, role: e.target.value })}
                        >
                          <option value="">Not imported — add as…</option>
                          {PROFILE_ROLES.filter((o) => o.value !== "ignore").map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Form.Select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </div>

      {/* ── 2. Legend ── */}
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <h6 className="fw-semibold mb-1">2. Zone legend</h6>
        <p className="text-muted small mb-3">
          Which colour on the sheet is which zone. Copy the codes from the sheet's own legend. A colour left out is still
          drawn, but as "zone not identified".
        </p>
        <Table responsive size="sm" className="align-middle" style={{ maxWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ width: 140 }}>Colour</th>
              <th style={{ width: 150 }}>Zone code</th>
              <th>Zone name</th>
              <th style={{ width: 120 }} className="text-end">On sample</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {settings.legend.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted small">No colours yet — add them from the sample's zoning colours below.</td>
              </tr>
            )}
            {settings.legend.map((l, i) => {
              const onSample = zoneColours.find((c) => c.fill === l.fill);
              return (
                <tr key={l.fill}>
                  <td className="small text-nowrap">{swatch(l.fill)} <code className="ms-1">{l.fill}</code></td>
                  <td>
                    <Form.Control size="sm" value={l.code} placeholder="e.g. R1" isInvalid={!l.code} maxLength={30}
                      onChange={(e) => setColour(i, { code: e.target.value.toUpperCase() })} />
                  </td>
                  <td>
                    <Form.Control size="sm" value={l.name} placeholder="e.g. Residential Zone" onChange={(e) => setColour(i, { name: e.target.value })} />
                  </td>
                  <td className="text-end small text-muted">{onSample ? `${fmt(Math.round(onSample.hectares))} ha` : "not on sample"}</td>
                  <td className="text-end">
                    <Button size="sm" variant="link" className="p-1 text-danger" onClick={() => removeColour(i)}><X size={14} /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {sample && (
          <>
            <h6 className="fw-semibold mt-3 mb-2 small text-uppercase text-muted">Colours in the sample's zoning layers</h6>
            {zoneColours.length === 0 ? (
              <p className="text-muted small mb-0">No zoning layer on the sample yet — set a rule to "Zoning" first.</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {zoneColours.map((c) => (
                  <Button
                    key={c.fill}
                    size="sm"
                    variant={legendFills.has(c.fill) ? "light" : "outline-secondary"}
                    disabled={legendFills.has(c.fill)}
                    className="d-flex align-items-center gap-2"
                    title={[...c.layers].join(", ")}
                    onClick={() => addColour(c.fill)}
                  >
                    {swatch(c.fill, 12)} {c.fill} · {fmt(Math.round(c.hectares))} ha
                    {legendFills.has(c.fill) ? " ✓" : " +"}
                  </Button>
                ))}
              </div>
            )}
            {sample.analysis?.sheet_legend?.length > 0 && (
              <div className="small text-muted mt-3">
                Legend read off the sheet (as hints):{" "}
                {sample.analysis.sheet_legend.map((l) => (
                  <span key={l.fill} className="me-3 text-nowrap">{swatch(l.fill, 10)} {l.label}</span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── 3. Labels, clean-up, review ── */}
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <h6 className="fw-semibold mb-3">3. Labels, clean-up and review</h6>
        <div className="row g-3" style={{ maxWidth: 900 }}>
          {[
            ["village_font", "Village names"],
            ["survey_font", "Survey numbers"],
            ["tp_font", "TP plot numbers"],
          ].map(([key, label]) => {
            const value = settings.labels[key] === "auto" ? "auto" : fontToSize(settings.labels[key]);
            const numbers = key !== "village_font";
            const known = textSizes.some((t) => String(t.size) === value);
            return (
              <div className="col-md-6" key={key}>
                <Form.Label className="small fw-medium">{label} — text size</Form.Label>
                <Form.Select
                  size="sm"
                  value={value}
                  onChange={(e) => change((s) => {
                    s.labels[key] = e.target.value === "auto" ? "auto" : sizeToFont(e.target.value);
                    return s;
                  })}
                >
                  <option value="">{key === "tp_font" ? "Any number inside the plot" : "None"}</option>
                  {numbers && (
                    <option value="auto">Automatic: the size and colour that number the most plots, per sheet</option>
                  )}
                  {value && value !== "auto" && !known && <option value={value}>{value} pt (not on the sample)</option>}
                  {textSizes.map((t) => (
                    <option key={t.size} value={String(t.size)}>
                      {t.size} pt · {fmt(t.n)} labels · e.g. {t.samples.slice(0, 4).join(", ")}
                    </option>
                  ))}
                </Form.Select>
              </div>
            );
          })}
          <div className="col-md-6">
            <Form.Label className="small fw-medium">Survey number pattern (optional)</Form.Label>
            <Form.Control
              size="sm"
              value={settings.labels.survey_pattern || ""}
              placeholder="default: ^[0-9]+[0-9A-Za-z/ ()-]*$"
              onChange={(e) => change((s) => { s.labels.survey_pattern = e.target.value || null; return s; })}
            />
          </div>
          <div className="col-md-6">
            <Form.Label className="small fw-medium">Close gaps in dashed village lines (m)</Form.Label>
            <Form.Control size="sm" type="number" min={0} max={50} value={settings.options.dashed_gap_m}
              onChange={(e) => change((s) => { s.options.dashed_gap_m = e.target.value; return s; })} />
          </div>
          <div className="col-md-6">
            <Form.Label className="small fw-medium">Close gaps in TP plot lines (m)</Form.Label>
            <Form.Control size="sm" type="number" min={0} max={10} step={0.5} value={settings.options.tp_gap_m ?? 0}
              onChange={(e) => change((s) => { s.options.tp_gap_m = e.target.value; return s; })} />
            <Form.Text muted>For sheets whose plot lines stop short of the road edges; 0 joins lines only where they meet.</Form.Text>
          </div>
          <div className="col-md-6">
            <Form.Label className="small fw-medium">Merge touching zone pieces of one colour</Form.Label>
            <Form.Select size="sm" value={settings.options.merge_zone_pieces || "auto"}
              onChange={(e) => change((s) => { s.options.merge_zone_pieces = e.target.value; return s; })}>
              <option value="auto">Automatically, when the zoning is a mesh of triangles</option>
              <option value="always">Always</option>
              <option value="never">Never (each filled shape is its own zone)</option>
            </Form.Select>
          </div>
          <div className="col-md-4">
            <Form.Label className="small fw-medium">"To check" below: % zones coded</Form.Label>
            <Form.Control size="sm" type="number" min={0} max={100} value={settings.thresholds.min_coded_pct}
              onChange={(e) => change((s) => { s.thresholds.min_coded_pct = e.target.value; return s; })} />
          </div>
          <div className="col-md-4">
            <Form.Label className="small fw-medium">… % parcels numbered</Form.Label>
            <Form.Control size="sm" type="number" min={0} max={100} value={settings.thresholds.min_numbered_pct}
              onChange={(e) => change((s) => { s.thresholds.min_numbered_pct = e.target.value; return s; })} />
          </div>
          <div className="col-md-4">
            <Form.Label className="small fw-medium">… or fit worse than (m)</Form.Label>
            <Form.Control size="sm" type="number" min={0} value={settings.thresholds.max_rms_m}
              onChange={(e) => change((s) => { s.thresholds.max_rms_m = e.target.value; return s; })} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2 common-shadow mt-4 p-4 d-flex flex-wrap align-items-center gap-2">
        <Button className="d-flex align-items-center gap-2" disabled={!!busy || !dirty} onClick={save}>
          {busy === "save" ? <Spinner size="sm" /> : <Save size={16} />} Save{dirty ? "" : "d"}
        </Button>
        <Button variant="success" className="d-flex align-items-center gap-2" disabled={!!busy || !sampleId} onClick={applyToSample}>
          {busy === "apply" ? <Spinner size="sm" /> : <Play size={16} />} {dirty ? "Save and apply" : "Apply"} to the sample import
        </Button>
        {dirty && <Alert variant="warning" className="small py-1 px-2 mb-0">Changes are not saved yet.</Alert>}
        <Button variant="outline-danger" size="sm" className="ms-auto d-flex align-items-center gap-1" onClick={() => setConfirmDelete(true)}>
          <Trash2 size={14} /> Delete profile
        </Button>
      </div>

      <Modal show={confirmDelete} onHide={() => setConfirmDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Delete "{profile.name}"?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">Imports that used it keep their settings and layers; they just no longer point to a profile.</Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button variant="danger" onClick={remove}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
